import {
  ChartType,
  CreateCustomSurveyTemplateRequest,
  CustomSurveyTemplate,
  QuestionType,
  TemplateScope,
} from '../../api/customSurveyTemplates';
import { categoriesOf } from '../CustomSurveyTemplatesPage/templatesView';

export type DraftQuestion = {
  /** Stabil nøkkel for React og for id-ene til feltene */
  key: number;
  questionText: string;
  questionType: QuestionType;
  isRequired: boolean;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel: string;
  scaleMaxLabel: string;
  chartType?: ChartType;
  category: string;
  options: string[];
};

export type Draft = {
  name: string;
  description: string;
  category: string;
  scope: TemplateScope;
  teamId?: number;
  questions: DraftQuestion[];
};

export type DraftError = {
  /** Id-en til feltet feilen gjelder, så lenken i feiloppsummeringen kan flytte fokus dit */
  fieldId: string;
  /** Spørsmålet feilen gjelder, så det kan åpnes */
  questionKey?: number;
  message: string;
  params?: Record<string, unknown>;
};

export const NPS_SCALE = { min: 0, max: 10 };
/** Skalaen kan gå fra 0 til 10, slik API-et godtar */
export const SCALE_LIMITS = { min: 0, max: 10 };

let nextKey = 0;

export const newQuestion = (isRequired = false): DraftQuestion => ({
  key: ++nextKey,
  questionText: '',
  questionType: QuestionType.LIKERT_SCALE,
  isRequired,
  scaleMin: 1,
  scaleMax: 5,
  scaleMinLabel: '',
  scaleMaxLabel: '',
  category: '',
  options: [],
});

export const emptyDraft = (): Draft => ({
  name: '',
  description: '',
  category: '',
  scope: TemplateScope.TENANT,
  questions: [newQuestion(true)],
});

/** Utkastet for en mal som finnes, så den kan redigeres */
export const fromTemplate = (template: CustomSurveyTemplate): Draft => ({
  name: template.name,
  description: template.description ?? '',
  category: template.category ?? '',
  scope: template.scope,
  teamId: template.scopeTeamId,
  questions: [...template.questions]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((question) => ({
      key: ++nextKey,
      questionText: question.questionText,
      questionType: question.questionType,
      isRequired: question.isRequired,
      scaleMin: question.scaleMin ?? 1,
      scaleMax: question.scaleMax ?? 5,
      scaleMinLabel: question.scaleMinLabel ?? '',
      scaleMaxLabel: question.scaleMaxLabel ?? '',
      chartType: question.chartType,
      category: question.category ?? '',
      options: [...(question.options ?? [])]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((option) => option.optionText),
    })),
});

export const isChoice = (type: QuestionType) =>
  type === QuestionType.MULTIPLE_CHOICE || type === QuestionType.MULTIPLE_SELECT;

export const questionFieldId = (key: number, field: string) => `question-${key}-${field}`;

/** Bytter svartype. Valgspørsmål får to alternativer å starte med. */
export const withType = (question: DraftQuestion, questionType: QuestionType, optionLabel: (n: number) => string) => ({
  ...question,
  questionType,
  chartType: questionType === question.questionType ? question.chartType : undefined,
  options: isChoice(questionType) && question.options.length === 0 ? [optionLabel(1), optionLabel(2)] : question.options,
});

export const copyQuestion = (question: DraftQuestion): DraftQuestion => ({
  ...question,
  key: ++nextKey,
  options: [...question.options],
});

export const move = <T,>(items: T[], from: number, to: number) => {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/** Verdiene på skalaen, slik den som svarer ser dem */
export const scaleSteps = (question: DraftQuestion) => {
  const { min, max } =
    question.questionType === QuestionType.NPS_SCALE ? NPS_SCALE : { min: question.scaleMin, max: question.scaleMax };
  if (!Number.isInteger(min) || !Number.isInteger(max) || min < SCALE_LIMITS.min || max > SCALE_LIMITS.max || max <= min) {
    return [];
  }
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
};

/** Kategoriene som finnes i malene fra før, både på malene og på spørsmålene */
export const knownCategories = (templates: CustomSurveyTemplate[]) =>
  [
    ...new Set([
      ...templates.flatMap(categoriesOf),
      ...templates.flatMap((template) =>
        template.questions.map((question) => question.category?.trim()).filter((category): category is string => !!category),
      ),
    ]),
  ].sort((a, b) => a.localeCompare(b, 'nb'));

/** Det som må rettes før malen kan opprettes, i samme rekkefølge som i skjemaet */
export const validateDraft = (draft: Draft): DraftError[] => {
  const errors: DraftError[] = [];
  if (!draft.name.trim()) errors.push({ fieldId: 'template-name', message: 'customSurveys.create.errors.name' });
  if (draft.scope === TemplateScope.TEAM && !draft.teamId) {
    errors.push({ fieldId: 'template-team', message: 'customSurveys.create.errors.team' });
  }

  draft.questions.forEach((question, index) => {
    const number = index + 1;
    const questionKey = question.key;
    if (!question.questionText.trim()) {
      errors.push({ fieldId: questionFieldId(questionKey, 'text'), questionKey, message: 'customSurveys.create.errors.questionText', params: { number } });
    }
    if (question.questionType === QuestionType.LIKERT_SCALE) {
      const { scaleMin: min, scaleMax: max } = question;
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < SCALE_LIMITS.min || max > SCALE_LIMITS.max) {
        errors.push({
          fieldId: questionFieldId(questionKey, Number.isInteger(min) && min >= SCALE_LIMITS.min ? 'max' : 'min'),
          questionKey,
          message: 'customSurveys.create.errors.scaleLimits',
          params: { number, ...SCALE_LIMITS },
        });
      } else if (max <= min) {
        errors.push({ fieldId: questionFieldId(questionKey, 'max'), questionKey, message: 'customSurveys.create.errors.scaleOrder', params: { number } });
      }
    }
    if (isChoice(question.questionType)) {
      const empty = question.options.findIndex((option) => !option.trim());
      if (question.options.filter((option) => option.trim()).length < 2) {
        errors.push({
          fieldId: questionFieldId(questionKey, empty >= 0 ? `option-${empty}` : 'add-option'),
          questionKey,
          message: 'customSurveys.create.errors.twoOptions',
          params: { number },
        });
      } else if (empty >= 0) {
        errors.push({ fieldId: questionFieldId(questionKey, `option-${empty}`), questionKey, message: 'customSurveys.create.errors.emptyOption', params: { number } });
      }
    }
  });

  if (draft.questions.length === 0) errors.push({ fieldId: 'add-question', message: 'customSurveys.create.errors.noQuestions' });
  return errors;
};

const orUndefined = (text: string) => text.trim() || undefined;

export const toRequest = (draft: Draft): CreateCustomSurveyTemplateRequest => ({
  name: draft.name.trim(),
  description: orUndefined(draft.description),
  category: orUndefined(draft.category),
  scope: draft.scope,
  scopeTeamId: draft.scope === TemplateScope.TEAM ? draft.teamId : undefined,
  questions: draft.questions.map((question, orderIndex) => {
    const scale =
      question.questionType === QuestionType.NPS_SCALE
        ? { scaleMin: NPS_SCALE.min, scaleMax: NPS_SCALE.max }
        : question.questionType === QuestionType.LIKERT_SCALE
          ? {
              scaleMin: question.scaleMin,
              scaleMax: question.scaleMax,
              scaleMinLabel: orUndefined(question.scaleMinLabel),
              scaleMaxLabel: orUndefined(question.scaleMaxLabel),
            }
          : {};
    return {
      questionText: question.questionText.trim(),
      questionType: question.questionType,
      isRequired: question.isRequired,
      orderIndex,
      category: orUndefined(question.category),
      chartType: question.chartType,
      ...scale,
      options: isChoice(question.questionType)
        ? question.options.map((optionText, index) => ({ optionText: optionText.trim(), orderIndex: index }))
        : undefined,
    };
  }),
});
